import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw } from "lucide-react";
import ReactMarkdown from "react-markdown";

interface AgentPlaybookPreviewProps {
  assessmentType: string;
  isTesting: boolean;
  playbook: string | null;
  onUpdatePlaybook: () => void;
  canUpdatePlaybook?: boolean;
  emptyMessage?: string;
  sampleSubmission?: string | null;
  isLoadingSampleSubmission?: boolean;
  sampleSubmissionMessage?: string;
}

export const AgentPlaybookPreview = ({
  assessmentType,
  isTesting,
  playbook,
  onUpdatePlaybook,
  canUpdatePlaybook = true,
  emptyMessage = "Preview will appear here",
  sampleSubmission,
  isLoadingSampleSubmission = false,
  sampleSubmissionMessage = "Select a hybrid assessment to view its sample submission",
}: AgentPlaybookPreviewProps) => {
  const showUpdateButton =
    assessmentType === "standalone" || assessmentType === "hybrid";
  const showSampleSubmission = assessmentType === "hybrid";

  return (
    <div className="w-1/2 flex flex-col pr-4 p-2 overflow-hidden">
      <Tabs defaultValue="playbook" className="flex flex-col h-full min-h-0 gap-0">
        <div className="flex items-center justify-between shrink-0 pb-2">
          {showSampleSubmission ? (
            <TabsList>
              <TabsTrigger value="playbook" className="text-xs">
                Agent playbook
              </TabsTrigger>
              <TabsTrigger value="sample" className="text-xs">
                Sample submission
              </TabsTrigger>
            </TabsList>
          ) : (
            <span className="text-sm font-medium">Agent playbook</span>
          )}
          {showUpdateButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onUpdatePlaybook}
              disabled={isTesting || !canUpdatePlaybook}
            >
              <RefreshCw
                className={`h-3 w-3 ${isTesting ? "animate-spin" : ""}`}
              />
              Update playbook
            </Button>
          )}
        </div>

        <TabsContent value="playbook" className="flex-1 min-h-0 mt-0">
          <div className="h-full min-h-0 bg-zinc-50 rounded-lg p-4 overflow-y-auto shadow-sm">
            {playbook ? (
              <div className="prose prose-sm max-w-none font-sans text-sm bg-zinc-50">
                <ReactMarkdown>{playbook}</ReactMarkdown>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-center text-muted-foreground text-sm">
                {emptyMessage}
              </div>
            )}
          </div>
        </TabsContent>

        {showSampleSubmission && (
          <TabsContent value="sample" className="flex-1 min-h-0 mt-0">
            <div className="h-full min-h-0 bg-zinc-50 rounded-lg p-4 overflow-y-auto shadow-sm">
              {isLoadingSampleSubmission ? (
                <div className="h-full flex items-center justify-center text-center text-muted-foreground text-sm">
                  Loading sample submission...
                </div>
              ) : sampleSubmission ? (
                <div className="prose prose-sm max-w-none font-sans text-sm bg-zinc-50">
                  <ReactMarkdown>{sampleSubmission}</ReactMarkdown>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-center text-muted-foreground text-sm">
                  {sampleSubmissionMessage}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
